/**
 * Feb 2026 · Client-side sortable table primitives.
 *
 * `useSortableRows(rows, defaultKey, defaultDir)` — returns
 *   {sorted, sortKey, sortDir, requestSort}. Compares numbers as numbers,
 *   ISO date strings lexicographically (works because YYYY-MM-DD), and
 *   everything else as case-insensitive locale strings. Empty/null values
 *   always sink to the bottom of ASC and top of DESC.
 *
 * `<SortableTH sortKey sortState onRequest ...>` — drop-in replacement
 *   for `<th>`. Renders the label + a chevron chip when this column is
 *   the active sort key. Clicking cycles DESC → ASC → DESC (never OFF,
 *   because Latest-on-Top is our platform default).
 *
 * Zero backend impact — operator toggles are memoised in React state,
 * so payload sizes and query patterns stay identical.
 */
import { useEffect, useMemo, useState } from "react";
import { CaretUpIcon, CaretDownIcon } from "@phosphor-icons/react";

const _STORAGE_PREFIX = "mes.sort.";

const _loadPersistedSort = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(_STORAGE_PREFIX + storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.sortKey !== undefined) return parsed;
  } catch (_e) { /* ignore corrupt storage */ }
  return null;
};

const _persistSort = (storageKey, sortKey, sortDir) => {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(_STORAGE_PREFIX + storageKey, JSON.stringify({ sortKey, sortDir }));
  } catch (_e) { /* quota or private mode — silently skip */ }
};

const _asNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const _getBy = (row, key) => {
  if (!key) return null;
  // Dot-path support so callers can pass e.g. "totals.grand_total".
  return key.split(".").reduce((o, k) => (o == null ? o : o[k]), row);
};

export function useSortableRows(rows, defaultKey = null, defaultDir = "desc", options = {}) {
  // P2 · Feb 2026 · Column-preference persistence.
  // Pass `{ storageKey: "sales-orders-grid" }` to remember the operator's
  // preferred sort across reloads (localStorage). Skipping storageKey keeps
  // the pre-existing session-only behaviour.
  const { storageKey = null } = options;
  const persisted = useMemo(() => _loadPersistedSort(storageKey), [storageKey]);

  const [sortKey, setSortKey] = useState(persisted?.sortKey ?? defaultKey);
  const [sortDir, setSortDir] = useState(persisted?.sortDir ?? defaultDir);

  useEffect(() => {
    _persistSort(storageKey, sortKey, sortDir);
  }, [storageKey, sortKey, sortDir]);

  const requestSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc"); // Every new column starts DESC (Latest-on-Top).
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !Array.isArray(rows) || rows.length < 2) return rows || [];
    const arr = [...rows];
    const sign = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = _getBy(a, sortKey);
      const vb = _getBy(b, sortKey);
      // Empties last regardless of direction (matches operator intuition).
      const emA = va === null || va === undefined || va === "";
      const emB = vb === null || vb === undefined || vb === "";
      if (emA && emB) return 0;
      if (emA) return 1;
      if (emB) return -1;
      const na = _asNum(va);
      const nb = _asNum(vb);
      if (na !== null && nb !== null) return (na - nb) * sign;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: "base", numeric: true }) * sign;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, requestSort };
}

export function SortableTH({ sortKey, sortState, onRequest, children, className = "", align = "left", testId }) {
  const active = sortState?.sortKey === sortKey;
  const dir = active ? sortState.sortDir : null;
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th
      data-testid={testId || `sort-${sortKey}`}
      onClick={() => onRequest(sortKey)}
      className={`cursor-pointer select-none ${alignClass} ${className} ${active ? "text-[var(--accent)]" : ""} hover:text-white transition`}
      title={`Sort by ${typeof children === "string" ? children : sortKey} · click to flip`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {children}
        {active && (dir === "asc"
          ? <CaretUpIcon size={10} weight="bold" />
          : <CaretDownIcon size={10} weight="bold" />)}
      </span>
    </th>
  );
}
